import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";

// ============================================================
// Request the vault raft-operator break-glass
// ============================================================

/**
 * Record ONE operator instruction to run the vault raft-operator break-glass on
 * one tenant: the agent recovers a root token from the tenant's sealed recovery
 * key, writes the raft-operator policy and its aws-auth role, revokes every root
 * token and proves the one it used is gone. Until that authority exists the
 * tenant's vault tier is never rolled.
 *
 * Off on every tenant until an operator records it here. It is delivered to the
 * agent on its next desired-state poll, and the agent reports back once the
 * grant has landed.
 */
export const AdminVibeTenantClusterRequestVaultRaftOperatorGrantBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /** Why. Required: it is the operator's stated reason on the admin audit row. */
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * Discriminated outcome. Every variant is a true answer; only a persistence
 * fault is an error.
 *
 *   - requested: a new instruction was recorded under `directiveId`.
 *   - already_requested: an instruction is outstanding — the same one is
 *     returned and nothing new is minted, so re-asking never runs the
 *     break-glass twice.
 *   - already_granted: the agent holds the authority already — reported
 *     `granted` on its last word, or the recorded instruction completed — and
 *     nothing was written. `directiveId` and `grantedAt` are the recorded
 *     instruction's, null when the authority came from bootstrap instead.
 *   - not_found: no cluster for that organization.
 */
export const AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcomeSchema =
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("requested"),
      directiveId: z.string().uuid(),
      requestedAt: z.string().datetime()
    }),
    z.object({
      kind: z.literal("already_requested"),
      directiveId: z.string().uuid(),
      requestedAt: z.string().datetime()
    }),
    z.object({
      kind: z.literal("already_granted"),
      directiveId: z.string().uuid().nullable(),
      grantedAt: z.string().datetime().nullable()
    }),
    z.object({ kind: z.literal("not_found") })
  ]);
export type AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcome = z.infer<
  typeof AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcomeSchema
>;

/** The outcome as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcomeReadSchema = z.union([
  AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(
      AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcomeSchema
    )
  })
]);
